import { useEffect, useState } from "react";
import { Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { StatusBadge } from "@/components/shared/StatusBadge";
import {
  OPTION_TONES,
  PROSPECT_FIELD_TYPES,
  type ProspectFieldType,
  type SelectOption,
} from "@/lib/prospectFields";
import { FIELD_TYPE_META } from "@/lib/prospectDisplay";
import { cn } from "@/lib/utils";

/**
 * Criar ou editar coluna personalizada de uma lista de leads frios: nome,
 * tipo (12 tipos) e, para seleção, as opções. A chave é gerada pelo hook a
 * partir do nome e nunca muda. Não grava: devolve os valores em `onSubmit`.
 */
export interface FieldDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Coluna existente = modo edição. */
  initial?: { label: string; type: ProspectFieldType; options: SelectOption[] };
  onSubmit: (value: { label: string; type: ProspectFieldType; options: SelectOption[] }) => void;
  isPending?: boolean;
}

export function FieldDialog({
  open,
  onOpenChange,
  initial,
  onSubmit,
  isPending,
}: FieldDialogProps) {
  const [label, setLabel] = useState("");
  const [type, setType] = useState<ProspectFieldType>("text");
  const [options, setOptions] = useState<SelectOption[]>([]);
  const [newOption, setNewOption] = useState("");

  useEffect(() => {
    if (!open) return;
    setLabel(initial?.label ?? "");
    setType(initial?.type ?? "text");
    setOptions(initial?.options ?? []);
    setNewOption("");
  }, [open, initial]);

  const hasOptions = type === "select" || type === "multi_select";

  const addOption = () => {
    const value = newOption.trim().slice(0, 80);
    if (!value || options.some((o) => o.value.toLowerCase() === value.toLowerCase())) return;
    setOptions((cur) => [
      ...cur,
      { value, label: value, tone: OPTION_TONES[(cur.length + 1) % OPTION_TONES.length] },
    ]);
    setNewOption("");
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!label.trim()) return;
    onSubmit({ label: label.trim().slice(0, 80), type, options: hasOptions ? options : [] });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[560px]">
        <form onSubmit={submit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>{initial ? "Editar coluna" : "Nova coluna"}</DialogTitle>
            <DialogDescription>
              {initial
                ? "Mudar o tipo não converte valores já gravados; células incompatíveis aparecem como texto."
                : "A coluna vale só para esta lista."}
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="field-label">Nome</Label>
            <Input
              id="field-label"
              autoFocus
              maxLength={80}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Ex.: Temperatura, Score, Dor identificada"
            />
          </div>

          <fieldset>
            <legend className="mb-2 text-sm font-medium">Tipo</legend>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {PROSPECT_FIELD_TYPES.map((t) => (
                <button
                  key={t}
                  type="button"
                  aria-pressed={type === t}
                  onClick={() => setType(t)}
                  className={cn(
                    "flex min-h-[60px] flex-col items-start gap-1 rounded-[10px] border p-2.5 text-left text-[12.5px] font-semibold",
                    type === t
                      ? "border-2 border-primary bg-accent text-primary"
                      : "border-border hover:bg-muted",
                  )}
                >
                  <span aria-hidden="true" className="text-[15px]">
                    {FIELD_TYPE_META[t].icon}
                  </span>
                  {FIELD_TYPE_META[t].label}
                </button>
              ))}
            </div>
          </fieldset>

          {hasOptions && (
            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium">Opções</span>
              <div className="flex flex-wrap gap-2">
                {options.map((o) => (
                  <StatusBadge
                    key={o.value}
                    shape="tag"
                    tone={o.tone ?? "neutral"}
                    className="gap-1 pr-1"
                  >
                    {o.label}
                    <button
                      type="button"
                      aria-label={`Remover opção ${o.label}`}
                      onClick={() => setOptions((cur) => cur.filter((x) => x.value !== o.value))}
                      className="rounded p-0.5 hover:bg-background/60"
                    >
                      <X className="h-3 w-3" aria-hidden="true" />
                    </button>
                  </StatusBadge>
                ))}
              </div>
              <div className="flex gap-2">
                <Label htmlFor="field-new-option" className="sr-only">
                  Nova opção
                </Label>
                <Input
                  id="field-new-option"
                  value={newOption}
                  maxLength={80}
                  onChange={(e) => setNewOption(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addOption();
                    }
                  }}
                  placeholder="Nova opção"
                />
                <Button type="button" variant="outline" onClick={addOption}>
                  <Plus className="mr-1 h-4 w-4" aria-hidden="true" /> Adicionar
                </Button>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={!label.trim() || isPending}>
              {initial ? "Salvar coluna" : "Criar coluna"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
